const UserUtils = {
    AUTH_STATE: {
        USER: 'user',
        GUEST: 'guest',
        LOGGED_OUT: 'logged-out'
    },

    INCLUDES: 'phone_number,admin,is_seller,credit_cards,license_plates,vehicles,favorites,date_joined,has_usable_password',

    /**
     * Normalizes a user's data after an API request or a user data change. Resetting to defaults is useful for when a user change
     * is initiated by HeroControls, for instance, and we need to clear all existing user data in the store. By default, this is not
     * true because this method is also called when just updating an already logged in user's data and we want to keep the values
     * that are already set on the user to what their previous setting is.
     *
     * @param {Object} data - The user data to normalize.
     * @param {Boolean} resetToDefaults - Whether to reset any user data that isn't passed to this method to its default value.
     * @returns {Object} - The newly normalized user data.
     */
    normalize({data, resetToDefaults = false}) {
        const user = {
            status: UserUtils.AUTH_STATE.USER,
            ...data
        };
        const creditCards = user.creditCards || [];
        const licensePlates = user.licensePlates || [];
        const favorites = [];

        if (user.credit_cards) {
            user.credit_cards.forEach(card => {
                if (!card.deleted) {
                    creditCards.push({
                        id: card.card_id,
                        type: card.card_type,
                        last4: card.card_last4,
                        isDefault: card.is_default
                    });
                }
            });
        }

        if (user.license_plates) {
            user.license_plates.forEach(plate => {
                licensePlates.push({
                    id: plate.license_plate_id,
                    licensePlate: plate.license_plate,
                    name: plate.name,
                    isDefault: plate.is_default
                });
            });
        }

        if (user.vehicles && user.vehicles.length && user.vehicles[0].vehicle_info_id) {
            user.vehicles = user.vehicles.map(({
                vehicle_info_id, make, make_pretty, model, model_pretty, year, is_default // eslint-disable-line camelcase
            }) => {
                return {
                    id: vehicle_info_id, // eslint-disable-line camelcase
                    make,
                    makeLabel: make_pretty, // eslint-disable-line camelcase
                    model,
                    modelLabel: model_pretty, // eslint-disable-line camelcase
                    year,
                    isDefault: is_default // eslint-disable-line camelcase
                };
            });
        }

        if (user.favorites && user.favorites.length) {
            user.favorites.forEach(favorite => {
                favorites.push({
                    id: favorite.id
                });
            });
        }

        user.creditCards = creditCards;
        user.licensePlates = licensePlates;
        user.favorites = favorites;

        if (user.first_name) {
            user.firstName = user.first_name;
        } else if (resetToDefaults) {
            user.firstName = null;
        }

        if (user.last_name) {
            user.lastName = user.last_name;
        } else if (resetToDefaults) {
            user.lastName = null;
        }

        if (user.display_name) {
            user.displayName = user.display_name;
        } else if (resetToDefaults) {
            user.displayName = null;
        }

        if (user.referral_token) {
            user.referralToken = user.referral_token;
        }

        if (user.date_joined) {
            user.created = user.date_joined;
        }

        if (user.just_created) {
            user.justCreated = user.just_created;
        } else {
            user.justCreated = false;
        }

        if (user.spothero_credit) {
            user.credit = user.spothero_credit;
        } else if (resetToDefaults) {
            user.credit = 0;
        }

        if (user.phone_number) {
            user.phoneNumber = user.phone_number;
        } else if (resetToDefaults) {
            user.phoneNumber = null;
        }

        if (user.admin) {
            user.isAdmin = user.admin;
        } else if (resetToDefaults) {
            user.isAdmin = false;
        }

        if (user.is_seller) {
            user.isSeller = user.is_seller;
        } else if (resetToDefaults) {
            user.isSeller = false;
        }

        if (user.has_usable_password) {
            user.hasUsablePassword = user.has_usable_password;
        }

        delete user.credit_cards;
        delete user.license_plates;
        delete user.first_name;
        delete user.last_name;
        delete user.display_name;
        delete user.referral_token;
        delete user.date_joined;
        delete user.just_created;
        delete user.spothero_credit;
        delete user.phone_number;
        delete user.admin;
        delete user.is_seller;
        delete user.has_usable_password;

        return user;
    }
};

export default UserUtils;
